import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useSearchParams } from "react-router-dom"
import ChevronDown from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-down.svg?react"
import Menu from "@/shared/icons/pikaicons/stroke/general/burger-menu-three.svg?react"
import { useViewContext } from "@/app/stores/viewContext"
import { useMainScroll } from "@/app/providers/MainScrollProvider"
import { getViewFromPathname } from "@/features/views/viewRegistry"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db, ObjectStatus } from "@/db/schema"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import { DynamicIcon } from "./DynamicIcon"
import { useAncestorTagStore } from "@/features/tags/ancestorTagStore"
import { TopNavMenu } from "./TopNavMenu"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"
import { MobileTopBar } from "@/shared/components/MobileTopBar"

// ---------------------------------------------------------------------------
// Scroll threshold — matches v1 (30px)
// ---------------------------------------------------------------------------
const SCROLL_THRESHOLD = 30

// ---------------------------------------------------------------------------
// View Info — title + icon metadata for center zone (v1 parity)
// ---------------------------------------------------------------------------

interface ViewInfo {
  title: string
  /** Icon type for static views (today, inbox, etc.) */
  staticIcon: RegisteredIconType | null
  /** "project" | "list" for TimelineIcon, "area" for StaticIcon, null for static views */
  containerType: "project" | "list" | "area" | null
  /** Container ID — needed for progress lookup from ancestorTagStore */
  containerId: string | null
  /** Container status — for DynamicIcon completed/canceled state */
  containerStatus: ObjectStatus
  /** Custom icon string from the container object */
  icon?: string
}

/**
 * Derive view identity from the current route using the View Registry.
 * Returns title + icon metadata so TopNavBar can render the correct icon.
 */
function useViewInfo(): ViewInfo {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const view = getViewFromPathname(pathname)

  // Parse container ID from the pathname since TopNavBar lives outside
  // the <Routes> tree and useParams() would return {}.
  const containerIdFromPath =
    pathname.match(/^\/(project|list|area|tag)\/(.+)/)?.[2] ?? null

  // For dynamic views, we need the container object from Dexie
  const isDynamic =
    view?.id === "project" || view?.id === "list" || view?.id === "area"
  const [container] = useLiveQueryImmediate(
    () =>
      isDynamic && containerIdFromPath
        ? db.totodo.get(containerIdFromPath)
        : undefined,
    ["topnav-info", containerIdFromPath ?? ""],
    undefined,
  )

  const title =
    isDynamic && container?.title
      ? container.title
      : view?.label
        ? t(view.label)
        : "ToToDo"

  // Static views — use the registered icon
  if (!isDynamic) {
    return {
      title,
      staticIcon: view?.icon ?? null,
      containerType: null,
      containerId: null,
      containerStatus: ObjectStatus.Unfinished,
      icon: undefined,
    }
  }

  // Dynamic views — map ObjectType to TimelineIcon type
  const containerType: "project" | "list" | "area" | null =
    view?.id === "project"
      ? "project"
      : view?.id === "list"
        ? "list"
        : view?.id === "area"
          ? "area"
          : null

  return {
    title,
    staticIcon: containerType === "area" ? "area" : null,
    containerType,
    containerId: containerIdFromPath,
    containerStatus: container?.status ?? ObjectStatus.Unfinished,
    icon: container?.icon,
  }
}

/**
 * Tracks whether the main scroll container has scrolled past the threshold.
 * Used to reveal the compact title in the top bar (v1 parity).
 */
function useScrolled(): boolean {
  const { scrollRef } = useMainScroll()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const handleScroll = () => {
      setScrolled(el.scrollTop > SCROLL_THRESHOLD)
    }

    el.addEventListener("scroll", handleScroll, { passive: true })
    return () => el.removeEventListener("scroll", handleScroll)
  }, [scrollRef])

  return scrolled
}

/**
 * Mobile-only top navigation bar (v1 parity).
 *
 * Scroll-reveal pattern (matching iOS large-title behaviour):
 * - At top: transparent background, title hidden (opacity 0).
 *   The large title visible in ViewShell header scrolls away naturally.
 * - After 30px scroll: compact title fades in, background becomes solid,
 *   subtle border appears. All transitions 300ms ease.
 *
 * Selection mode (iOS standard pattern):
 * - Left: "Select All" text button
 * - Center: "{count} Selected"
 * - Right: "Done" text button
 *
 * Three zones: left (hamburger), center (view title), right (placeholder).
 * Hidden on desktop — the AppLayout only renders this on mobile.
 */
export function TopNavBar() {
  const { t } = useTranslation()
  const setSidebarOpen = useViewContext((s) => s.setSidebarOpen)
  const setSearchOpen = useViewContext((s) => s.setSearchOpen)
  const selectedTaskIds = useViewContext((s) => s.selectedTaskIds)
  const visibleTaskIds = useViewContext((s) => s.visibleTaskIds)
  const selectAll = useViewContext((s) => s.selectAll)
  const clearSelection = useViewContext((s) => s.clearSelection)
  const viewInfo = useViewInfo()
  const scrolled = useScrolled()
  const [searchParams] = useSearchParams()
  const taskOpen = !!searchParams.get("tid")
  const { value: isColorVariant } = useSettingBoolean("#theme-variant-color")
  const iconMode = isColorVariant ? ("color" as const) : ("monochrome" as const)

  const isSelectionMode = selectedTaskIds.length > 0
  // "All selected" when every visible task is selected
  const allSelected =
    isSelectionMode &&
    visibleTaskIds.length > 0 &&
    visibleTaskIds.every((id) => selectedTaskIds.includes(id))

  // Progress data from ancestorTagStore (populated by sidebar — no extra DB query)
  const progress = useAncestorTagStore(
    (s) =>
      viewInfo.containerId
        ? s.containerProgress[viewInfo.containerId]
        : undefined,
  )

  // When a task is open, collapse to just the status bar height (v1 parity).
  // The inner controls fade out and the bar becomes a transparent spacer.
  // In selection mode, always show solid background (iOS standard).
  //
  // Persistent wrapper handles animated height collapse (task-open → status bar only).
  // MobileTopBar renders at full 44px; wrapper clips via overflow:hidden.
  return (
    <div
      className="shrink-0 overflow-hidden transition-[height] duration-300 ease-in-out"
      style={{
        height: taskOpen
          ? "var(--topbar-status-inset, var(--status-bar-height, 0px))"
          : "calc(var(--topbar-status-inset, var(--status-bar-height, 0px)) + 50px)",
      }}
    >
      {isSelectionMode ? (
        <MobileTopBar
          data-selection-toolbar
          bordered
          left={
            <button
              onClick={() => {
                if (allSelected) {
                  clearSelection()
                } else {
                  selectAll()
                }
              }}
              className="shrink-0 text-[15px] font-medium text-primary"
              aria-label={allSelected ? t("Deselect All") : t("Select All")}
            >
              {allSelected ? t("Deselect All") : t("Select All")}
            </button>
          }
          center={
            <span className="text-[17px] font-semibold leading-tight">
              {t("{{count}} Selected", { count: selectedTaskIds.length })}
            </span>
          }
          right={
            <button
              onClick={() => clearSelection()}
              className="shrink-0 text-[15px] font-semibold text-primary"
              aria-label={t("Done")}
            >
              {t("Done")}
            </button>
          }
        />
      ) : (
        <MobileTopBar
          transparent={!scrolled}
          bordered={false}
          style={{
            borderBottom: scrolled
              ? "1px solid var(--border)"
              : "1px solid transparent",
            transition:
              "background-color 300ms ease-in-out, border-color 300ms ease-in-out",
          }}
          left={
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              aria-label={t("Open sidebar menu")}
            >
              <Menu className="size-6" />
            </button>
          }
          center={
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center justify-center gap-2"
              style={{
                opacity: scrolled ? 1 : 0,
                pointerEvents: scrolled ? "auto" : "none",
                transition: "opacity 300ms ease-in-out",
                willChange: "opacity",
              }}
              aria-label={t("Search")}
            >
              {viewInfo.staticIcon && (
                <StaticIcon
                  type={viewInfo.staticIcon}
                  active={viewInfo.containerType !== "area"}
                  fillOnActive={viewInfo.containerType !== "area"}
                  size={22}
                  mode={iconMode}
                />
              )}
              {(viewInfo.containerType === "project" ||
                viewInfo.containerType === "list") && (
                <DynamicIcon
                  icon={viewInfo.icon}
                  type={viewInfo.containerType}
                  percentage={
                    progress && progress.total > 0
                      ? Math.round(
                          (progress.completed / progress.total) * 100,
                        )
                      : 0
                  }
                  unfinished={progress?.unfinished ?? 0}
                  status={progress?.status ?? viewInfo.containerStatus}
                  size="medium"
                  color="var(--primary)"
                />
              )}
              <span className="truncate text-[17px] font-semibold leading-tight">
                {viewInfo.title}
              </span>
              <ChevronDown className="size-4.5 shrink-0 text-muted-foreground" />
            </button>
          }
          right={<TopNavMenu />}
        />
      )}
    </div>
  )
}
